import _cloneDeep from 'lodash/cloneDeep';
import _isNil from 'lodash/isNil';
import _orderBy from 'lodash/orderBy';
import {
    DEFAULT_TARGET_INSPECTION_RATE,
    DEFAULT_TARGET_YIELD_RATE,
    IQcStatusOption,
} from '@Data/qualityControl/Interfaces';
import { IUser } from '@Data/users/Interfaces';
import { folderEndPoints } from '@Services/data/ApiHandler';
import { KoruRoutes } from '@Services/navigation/Paths';
import store from '@Services/redux/Store';
import { IFolder, IZoomToBoundsId } from './Interfaces';
import { FolderActions } from './Reducer';

const Api = {
    getFolders: async (
        projectId: number,
        callback?: (data: any) => void,
        doTriggerRefresh = false
    ) => {
        store.dispatch(FolderActions.setLoadingFolders(true));
        const response = await folderEndPoints.getFolders(projectId);
        if (response.isSuccess) {
            const folders: IFolder[] = response.data.data.folders;
            let parsedData = _orderBy(
                folders,
                (folder) => folder.folder_name,
                'asc'
            );

            // add the default target inspection and yield rates if we are in the qc2 block
            if (KoruRoutes.qc.matcher.test(window.location.href)) {
                parsedData = parsedData.map((folder) => {
                    const cloned = _cloneDeep(folder);
                    if (_isNil(folder.target_inspection_rate)) {
                        cloned.target_inspection_rate =
                            DEFAULT_TARGET_INSPECTION_RATE;
                    }

                    if (_isNil(folder.target_pass_yield)) {
                        cloned.target_pass_yield = DEFAULT_TARGET_YIELD_RATE;
                    }

                    return cloned;
                });
            }
            store.dispatch(FolderActions.updateFolders(parsedData));
            store.dispatch(FolderActions.setLoadingFolders(false));

            if (callback) {
                callback(parsedData);
            }

            if (doTriggerRefresh) {
                store.dispatch(FolderActions.tryRefreshData());
            }

            return parsedData;
        }
    },
    reset: () => {
        store.dispatch(FolderActions.reset());
    },
    clearSelectedState: () => {
        store.dispatch(FolderActions.clearSelectedState());
    },
    setMultipleFolderVisibility: (
        folders: Array<IFolder>,
        callback?: () => void
    ) => {
        store.dispatch(FolderActions.setMultipleFoldersVisible(folders));
        if (callback) {
            callback();
        }
    },
    toggleFolderVisibility: (folder: IFolder, callback?: () => void) => {
        store.dispatch(FolderActions.toggleFolderVisibility(folder));

        if (callback) {
            callback();
        }
    },
    clearVisibleFolders: (callback?: () => void) => {
        store.dispatch(FolderActions.clearVisibleFolders());
        if (callback) {
            callback();
        }
    },
    setAllFoldersVisibility: (isVisible: boolean) => {
        store.dispatch(FolderActions.setAllFoldersVisibility(isVisible));
    },
    getFolderFields: async (folderId: string, callback?: () => void) => {
        const response = await folderEndPoints.getFolderFields(folderId);
        if (response.isSuccess) {
            store.dispatch(FolderActions.setFolderFields(response.data.data));

            if (callback) {
                callback();
            }
        }
    },
    clearFolderFields: () => {
        store.dispatch(FolderActions.setFolderFields([]));
    },
    zoomToBounds: (folderId: string, folderIds: Array<string>) => {
        const zoomToBoundsObj: IZoomToBoundsId = {
            folderId: folderId,
            folderIds: folderIds,
            key: Math.random().toString(16), // generate a key so that the same wp's bounds can be viewed more than once sequentially
        };
        store.dispatch(FolderActions.setZoomToFolderBoundsId(zoomToBoundsObj));
    },
    clearZoomToBounds: () => {
        store.dispatch(FolderActions.setZoomToFolderBoundsId(null));
    },
    createFolder: async (
        folderName: string,
        projectId: number,
        callback?: (data: any) => void
    ) => {
        const body = {
            folder_name: folderName,
            project_fk: projectId,
            folder_type: 'user',
        };

        const response = await folderEndPoints.createFolder(body);
        if (response.isSuccess) {
            if (callback) {
                callback(response.data.folder_id);
            }
        }
    },
    editFolderName: async (
        folderName: string,
        existingFolder: IFolder,
        callback?: () => void
    ) => {
        const body = {
            folder_name: folderName,
            folder_type: existingFolder.folder_type,
        };

        const response = await folderEndPoints.editFolder(
            body,
            existingFolder.folder_id
        );
        if (response.isSuccess) {
            if (callback) {
                callback();
            }
        }
    },
    editFolderDetails: async (
        updateFolderDetails: any,
        existingFolder: IFolder,
        callback?: () => void
    ) => {
        const body = {
            ...updateFolderDetails,
            // folder_name: existingFolder.folder_name,
            // folder_type: existingFolder.folder_type,
            // project_fk: existingFolder.project_fk,
        };

        const response = await folderEndPoints.editFolder(
            body,
            existingFolder.folder_id
        );
        if (response.isSuccess) {
            if (callback) {
                callback();
            }
        }
    },
    setSelectedFolder: (folderId: string) => {
        store.dispatch(FolderActions.setSelectedFolderId(folderId));
    },
    setSelectedFolderIds: (folderIds: Array<string>) => {
        store.dispatch(FolderActions.setSelectedFolderIds(folderIds));
    },
    setExpandedFolderIds: (folderIds: Array<string>) => {
        store.dispatch(FolderActions.setExpandedFolderIds(folderIds));
    },
    createSubset: async (
        folder: IFolder,
        subsetAmount: number,
        callback?: () => void
    ) => {
        const response = await folderEndPoints.createFolderSubset(
            folder.folder_id,
            subsetAmount
        );

        if (response.isSuccess) {
            if (callback) {
                callback();
            }
        }
    },
    updateStatusAndAssignment: async (
        newAnalyst: IUser,
        newStatus: IQcStatusOption,
        folderId: string,
        projectId: number
    ) => {
        const payload = {
            ike_analyst_name: newAnalyst.name,
            folder_fk: folderId,
            project_fk: projectId,
            status: newStatus.display_name,
        };

        // todo currently we dont set the status due to data saving issues
        // once we have a solutino from the platform team for this and
        // have a new implementation for updateStatusAndAssignment this can be reinstated
        //const response = await folderEndPoints.updateStatusAndAssignment(
        //    payload
        //);
        //return response.isSuccess;
    },
};

export default Api;
